/*
 * CASA SOFIA — rotating announcement bar.
 *
 * Ports app/components/AnnouncementBar.tsx to a framework-free custom element.
 * One short message visible at a time; slides are stacked in a single grid cell
 * and cross-fade by opacity (base.css animates `.announce-slide[data-active]`).
 * Auto-advances every `data-announce-interval` seconds (default 6), pauses on
 * hover/focus, and skips auto-rotation under prefers-reduced-motion. Gold
 * prev/next chevrons step manually (and are the only control when motion is
 * reduced). With a single message there is nothing to rotate.
 *
 * The section (sections/announcement-bar.liquid) renders the markup; this module
 * only wires behaviour onto the `<announcement-bar data-announce>` element.
 */

const DEFAULT_INTERVAL_S = 6;

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.paused = false;
    this.timer = null;
    this._tick = this._tick.bind(this);
    this._pause = this._pause.bind(this);
    this._resume = this._resume.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.announce-slide'));
    if (this.slides.length <= 1) return; // nothing to rotate

    // Start on whichever slide the section marked active (always the first).
    const active = this.slides.findIndex(
      (s) => s.getAttribute('data-active') === 'true',
    );
    this.index = active >= 0 ? active : 0;

    const seconds =
      Number(this.getAttribute('data-announce-interval')) || DEFAULT_INTERVAL_S;
    this.intervalMs = seconds * 1000;

    // Prev / next chevrons.
    const prev = this.querySelector('[data-announce-prev]');
    const next = this.querySelector('[data-announce-next]');
    if (prev) prev.addEventListener('click', () => this._go(this.index - 1));
    if (next) next.addEventListener('click', () => this._go(this.index + 1));

    // Pause on hover/focus (focus listeners use capture so child focus counts).
    this.addEventListener('mouseenter', this._pause);
    this.addEventListener('mouseleave', this._resume);
    this.addEventListener('focus', this._pause, true);
    this.addEventListener('blur', this._resume, true);

    this._start();
  }

  disconnectedCallback() {
    this._stop();
    this.removeEventListener('mouseenter', this._pause);
    this.removeEventListener('mouseleave', this._resume);
    this.removeEventListener('focus', this._pause, true);
    this.removeEventListener('blur', this._resume, true);
  }

  /* ── rotation ───────────────────────────────────────────────────────────── */
  _reducedMotion() {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  _start() {
    this._stop();
    if (this.paused || this._reducedMotion()) return;
    this.timer = window.setInterval(this._tick, this.intervalMs);
  }

  _stop() {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  _tick() {
    this._show(this.index + 1);
  }

  /* Manual step (chevrons): move, then restart the timer so the next auto-
   * advance is a full interval away. */
  _go(next) {
    this._show(next);
    this._start();
  }

  _show(next) {
    const count = this.slides.length;
    const i = ((next % count) + count) % count;
    if (i === this.index) return;
    this.slides.forEach((slide, n) => {
      if (n === i) {
        slide.setAttribute('data-active', 'true');
        slide.removeAttribute('aria-hidden');
      } else {
        slide.removeAttribute('data-active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });
    this.index = i;
  }

  _pause() {
    this.paused = true;
    this._stop();
  }

  _resume() {
    this.paused = false;
    this._start();
  }
}

if (
  typeof customElements !== 'undefined' &&
  !customElements.get('announcement-bar')
) {
  customElements.define('announcement-bar', AnnouncementBar);
}
